import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Receipt, Plus, Loader2, Sparkles, BookOpen, TrendingUp, Calendar, Trash2,
} from "lucide-react";
import { formatCurrency } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CA_PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];

export default function Expenses() {
  const [tab, setTab] = useState("list");
  const [cats, setCats] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [guide, setGuide] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const refresh = async () => {
    try {
      const [c, r, s, g] = await Promise.all([
        axios.get(`${API}/expenses/categories`),
        axios.get(`${API}/expenses`),
        axios.get(`${API}/expenses/summary?year=${year}`),
        axios.get(`${API}/expenses/tax-guide`),
      ]);
      setCats(c.data); setRows(r.data); setSummary(s.data); setGuide(g.data);
    } catch { toast.error("Failed to load expenses"); }
  };
  useEffect(() => { refresh(); }, [year]);

  const blank = () => ({
    category_id: cats[0]?.id || null,
    expense_date: new Date().toISOString().slice(0, 10),
    amount: 0, vendor_name: "", province: "ON",
    tax_amount: 0, payment_method: "credit", is_tax_deductible: true,
    notes: "", business_purpose: "",
  });

  const save = async () => {
    if (!edit?.amount || edit.amount <= 0) { toast.error("Amount required"); return; }
    try {
      if (edit.id) await axios.put(`${API}/expenses/${edit.id}`, edit);
      else await axios.post(`${API}/expenses`, edit);
      toast.success("Saved"); setOpen(false); setEdit(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await axios.delete(`${API}/expenses/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const onReceiptScan = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/expenses/scan-receipt`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const f = r.data.fields || {};
      const guess = cats.find((c) => c.name === f.category_suggestion);
      setEdit({
        ...blank(),
        category_id: guess?.id || blank().category_id,
        expense_date: f.expense_date || blank().expense_date,
        amount: Number(f.amount) || 0,
        tax_amount: Number(f.tax_amount) || 0,
        vendor_name: f.vendor_name || "",
        province: (f.province_guess || "ON").toUpperCase(),
        notes: f.notes || "",
      });
      setOpen(true);
      toast.success(`Scanned receipt (confidence ${Math.round((f.confidence || 0) * 100)}%) — review and save.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10"><Receipt className="h-7 w-7 text-primary" /></div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Business Expenses</h1>
            <p className="text-muted-foreground">Track expenses, scan receipts with AI, and prep for tax time.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => onReceiptScan(e.target.files?.[0])} data-testid="receipt-scan-input" />
            <span className={`inline-flex items-center px-3 py-2 rounded-md border-2 border-dashed border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-sm font-medium ${scanning ? "opacity-50" : ""}`}>
              {scanning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {scanning ? "Scanning…" : "AI scan receipt"}
            </span>
          </label>
          <Button onClick={() => { setEdit(blank()); setOpen(true); }} data-testid="add-expense-btn">
            <Plus className="h-4 w-4 mr-1" /> Add expense
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="list" data-testid="tab-list"><Receipt className="h-4 w-4 mr-1" /> Expenses</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary"><TrendingUp className="h-4 w-4 mr-1" /> Year-end summary</TabsTrigger>
          <TabsTrigger value="guide" data-testid="tab-guide"><BookOpen className="h-4 w-4 mr-1" /> Tax write-off guide</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Card>
            <CardHeader><CardTitle>{rows.length} expenses</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Tax</TableHead>
                    <TableHead>Province</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">{r.expense_date}</TableCell>
                      <TableCell className="text-sm font-medium">{r.vendor_name || "—"}</TableCell>
                      <TableCell><Badge variant="outline">{r.category_name || "Uncategorized"}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(r.amount)}</TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(r.tax_amount)}</TableCell>
                      <TableCell><Badge variant="secondary">{r.province || "—"}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => { setEdit(r); setOpen(true); }} className="mr-1">Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No expenses yet. Click <strong>AI scan receipt</strong> or <strong>Add expense</strong> above.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <div className="flex justify-between flex-wrap gap-2 items-center">
                <div><CardTitle>Year-end summary</CardTitle><CardDescription>Aggregate by category, with Canadian meals 50%-rule adjustment.</CardDescription></div>
                <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-24" />
              </div>
            </CardHeader>
            <CardContent>
              {summary && (
                <div className="grid gap-4 md:grid-cols-3 mb-6">
                  <Card><CardHeader className="pb-2"><CardDescription>Total spend ({summary.year})</CardDescription><CardTitle className="text-3xl">{formatCurrency(summary.total_spend)}</CardTitle></CardHeader></Card>
                  <Card><CardHeader className="pb-2"><CardDescription>CA-adjusted deductible</CardDescription><CardTitle className="text-3xl text-emerald-600 dark:text-emerald-400">{formatCurrency(summary.deductible_canada_adjusted)}</CardTitle></CardHeader></Card>
                  <Card><CardHeader className="pb-2"><CardDescription>Meals (50%-rule applied)</CardDescription><CardTitle className="text-3xl">{formatCurrency(summary.meal_spend)}</CardTitle></CardHeader></Card>
                </div>
              )}
              <Table>
                <TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(summary?.categories || []).map((c) => (
                    <TableRow key={c.name}>
                      <TableCell>{c.name}{c.is_meal && <Badge variant="outline" className="ml-2 text-[10px]">Meal · 50%</Badge>}</TableCell>
                      <TableCell className="text-right">{c.count}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(c.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="guide">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5" /> Canadian Tax Write-Off Guide</CardTitle>
              <CardDescription>
                {guide && <>Last reviewed <strong>{guide.last_reviewed}</strong> · {guide.jurisdiction}</>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {guide && (
                <>
                  <p className="text-xs text-muted-foreground italic mb-4">{guide.disclaimer}</p>
                  <div className="space-y-3">
                    {guide.sections.map((s) => (
                      <div key={s.category} className="p-3 rounded-lg border bg-card">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                          <h3 className="font-semibold">{s.category}</h3>
                          <Badge variant="default">{s.deductibility}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{s.notes}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEdit(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{edit?.id ? "Edit expense" : "New expense"}</DialogTitle>
            <DialogDescription>Province auto-selects the right Canadian tax rate.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid gap-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={edit.expense_date || ""} onChange={(e) => setEdit({ ...edit, expense_date: e.target.value })} />
                </div>
                <div>
                  <Label>Amount</Label>
                  <Input type="number" step="0.01" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: parseFloat(e.target.value) || 0 })} data-testid="expense-amount-input" />
                </div>
                <div>
                  <Label>Tax amount</Label>
                  <Input type="number" step="0.01" value={edit.tax_amount} onChange={(e) => setEdit({ ...edit, tax_amount: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={edit.category_id || ""} onValueChange={(v) => setEdit({ ...edit, category_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Province</Label>
                  <Select value={edit.province || "ON"} onValueChange={(v) => setEdit({ ...edit, province: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CA_PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment method</Label>
                  <Select value={edit.payment_method || "credit"} onValueChange={(v) => setEdit({ ...edit, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="debit">Debit</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="etransfer">E-transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Vendor</Label><Input value={edit.vendor_name || ""} onChange={(e) => setEdit({ ...edit, vendor_name: e.target.value })} /></div>
              <div><Label>Business purpose (required for meals/entertainment per CRA)</Label><Input value={edit.business_purpose || ""} onChange={(e) => setEdit({ ...edit, business_purpose: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={!!edit.is_tax_deductible} onCheckedChange={(v) => setEdit({ ...edit, is_tax_deductible: !!v })} /> Tax-deductible
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-expense-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
